<template>
  <div class="BirthDatePickerComponent">
    <!-- Labels only for accesibility purposes -->
    <label
      :for="`${id}-day`"
      v-html="`${baseLabel || ''} ${t('common.dates.day')}`"
    ></label>
    <label
      :for="`${id}-month`"
      v-html="`${baseLabel || ''} ${t('common.dates.month')}`"
    ></label>
    <label
      :for="`${id}-year`"
      v-html="`${baseLabel || ''} ${t('common.dates.year')}`"
    ></label>
    <label
      :for="`${id}-full-date`"
      v-html="`${baseLabel || ''} ${t('common.dates.full-date')}`"
    ></label>

    <div class="inputs">
      <v-text-field
        ref="$day"
        v-model="day"
        :aria-describedby="undefined"
        :id="`${id}-day`"
        :placeholder="t('common.dates.day')"
        :name="`${id}-day`"
        :maxLength="DAY_FIELD_SIZE"
        :max="maxValidDay"
        :rules="dayValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        :inputmode="InputmodeTypes.Numeric"
        @keydown="isNumberKey($event)"
        @change="onChange(setDay, $event.target.value)"
        @input="inputDay($event.target.value)"
      />

      <v-text-field
        ref="$month"
        v-model="month"
        :aria-describedby="undefined"
        :id="`${id}-month`"
        :placeholder="t('common.dates.month')"
        :name="`${id}-month`"
        :maxLength="MONTH_FIELD_SIZE"
        :max="maxValidMonth"
        :rules="monthValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        :inputmode="InputmodeTypes.Numeric"
        @keydown="isNumberKey"
        @change="onChange(setMonth, $event.target.value)"
        @input="inputMonth($event.target.value)"
      />

      <v-text-field
        ref="$year"
        v-model="year"
        :aria-describedby="undefined"
        :id="`${id}-year`"
        :placeholder="t('common.dates.year')"
        :name="`${id}-year`"
        :maxLength="YEAR_FIELD_SIZE"
        :max="maxValidYear"
        :rules="yearValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        :inputmode="InputmodeTypes.Numeric"
        @keydown="isNumberKey"
        @change="onChange(setYear, $event.target.value)"
      />
    </div>

    <v-text-field
      ref="$date"
      :aria-describedby="undefined"
      :id="`${id}-full-date`"
      class="full-date"
      v-model="inputDateFormat"
      :rules="dateValidations"
      :hide-details="true"
    />

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </div>
</template>

<script setup lang="ts">
  import InputmodeTypes from '@/modules/core/models/InputmodeTypes'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { areEquals, maxDaysByMonth } from '@/utils/date.utils'
  import { isNumberKey } from '@/utils/input.utils'
  import { addNecessaryLeadingZeros } from '@/utils/textUtils'
  import { isNumber, validDate } from '@/utils/validations.utils'
  import type { Ref } from 'vue'
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'
  import ErrorMessageComponent from './ErrorMessageComponent.vue'

  const DEFAULT_MAX_DAYS = 31
  const DEFAULT_MAX_MONTH = 12
  const DAY_FIELD_SIZE = 2
  const MONTH_FIELD_SIZE = 2
  const YEAR_FIELD_SIZE = 4

  const { t } = useI18n()

  const emit = defineEmits(['update:modelValue'])

  interface PropsInterface {
    id: string
    modelValue: Date | null
    rules: ValidationRuleType[]
    baseLabel?: string
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: null,
    rules: () => []
  })

  let observer: MutationObserver | null = null

  onMounted(async () => {
    // Esperar render inicial
    await nextTick()
    const rootElementDay = document.getElementById(props.id + '-day')
      ?.parentElement as HTMLElement | null
    const rootElementMonth = document.getElementById(props.id + '-month')
      ?.parentElement as HTMLElement | null
    const rootElementYear = document.getElementById(props.id + '-year')
      ?.parentElement as HTMLElement | null
    const rootElementFullDate = document.getElementById(props.id + '-full-date')
      ?.parentElement as HTMLElement | null
    observer = startDuplicatedLabelControl([
      rootElementDay,
      rootElementMonth,
      rootElementYear,
      rootElementFullDate
    ])
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })

  const $date: Ref<any> = ref(null)
  const $day: Ref<any> = ref(null)
  const $month: Ref<any> = ref(null)
  const $year: Ref<any> = ref(null)

  const day: Ref<string> = ref('')
  const month: Ref<string> = ref('')
  const year: Ref<string> = ref('')

  const validationRules = {
    maxDay: (value: string) =>
      (isNumber(value) && Number(value) <= maxValidDay.value) || t('common.validations.date'),
    maxMonth: (value: string) =>
      (isNumber(value) && Number(value) <= maxValidMonth.value) || t('common.validations.date'),
    maxYear: (value: string) =>
      (isNumber(value) && Number(value) <= maxValidYear.value) || t('common.validations.date'),
    positiveNumber: (value: string) => isNumber(value) && Number(value) > 0,
    validDate: (value: string) =>
      value === '//'
        ? t('common.validations.date-empty')
        : validDate(value) || t('common.validations.date')
  }

  const dayValidations = [validationRules.maxDay, validationRules.positiveNumber]
  const monthValidations = [validationRules.maxMonth, validationRules.positiveNumber]
  const yearValidations = [validationRules.maxYear, validationRules.positiveNumber]
  const dateValidations = [validationRules.validDate, ...props.rules]

  const fieldValidationErrorMessage = computed(() => {
    if (!$day?.value || !$month?.value || !$year?.value) return ''

    return (
      $date.value?.errorMessages[0] ||
      $day.value?.errorMessages[0] ||
      $month.value?.errorMessages[0] ||
      $year.value?.errorMessages[0] ||
      ''
    )
  })

  const inputDateFormat = computed(() => `${day.value}/${month.value}/${year.value}`)

  const date = computed(() => {
    if (!isValidDate()) {
      return null
    }
    const monthNew = Number(month.value)
    const dayNew = Number(day.value)

    return new Date(
      `${year.value}/${monthNew < 10 ? '0' + monthNew : monthNew}/${
        dayNew < 10 ? '0' + dayNew : dayNew
      }`
    )
  })

  const maxValidDay = computed(() => {
    if (!month.value || !year.value) {
      return DEFAULT_MAX_DAYS
    }

    return maxDaysByMonth(Number(month.value), Number(year.value))
  })

  const maxValidMonth = computed(() => {
    if (!year.value || Number(year.value) < Number(maxValidYear.value)) {
      return DEFAULT_MAX_MONTH
    }

    const currentMonth = new Date().getMonth() + 1
    return currentMonth
  })

  const maxValidYear = computed(() => {
    const currentYear = new Date().getFullYear()
    return currentYear
  })

  watch(
    () => props.modelValue,
    (value) => {
      setDay(String(value?.getDate?.() ?? ''))
      setYear(String(value?.getFullYear?.() ?? ''))

      const month = value?.getMonth?.()
      setMonth(String(month || month === 0 ? month + 1 : ''))
    },
    { immediate: true }
  )

  function onChange(setterCallback: (value: string) => void, value: string) {
    setterCallback(value)

    if (day.value && month.value && year.value && year.value.length === 4 && isValidDate()) {
      emitDate()
    }
  }

  function inputDay(value: string) {
    if (runValidation(dayValidations, value) && value.length > 1) {
      $month.value.focus()
    }
  }

  function setDay(value: string) {
    if (!runValidation(dayValidations, value) || !value.length) {
      return
    }

    day.value = addNecessaryLeadingZeros(value, DAY_FIELD_SIZE)
  }

  function inputMonth(value: string) {
    //if(value === "1") return;
    if (runValidation(monthValidations, value) && value.length > 1) {
      $year.value.focus()
    }
  }

  function setMonth(value: string) {
    if (!runValidation(monthValidations, value) || !value.length) {
      return
    }

    month.value = addNecessaryLeadingZeros(value, MONTH_FIELD_SIZE)
  }

  function setYear(value: string) {
    if (!runValidation(yearValidations, value)) {
      return
    }

    year.value = addNecessaryLeadingZeros(value, YEAR_FIELD_SIZE)
  }

  function isValidDate() {
    return (
      runValidation(dayValidations, day.value) &&
      runValidation(monthValidations, month.value) &&
      runValidation(yearValidations, year.value) &&
      runValidation(dateValidations, `${day.value}/${month.value}/${year.value}`)
    )
  }

  // todo - use as util
  function runValidation(rules: ValidationRuleType[], value: any): boolean {
    return rules.every((rule) => rule(value) === true)
  }

  function isInvalidDateObject(date: Date | null) {
    return String(date) === 'Invalid Date'
  }

  function emitDate() {
    if (areEquals(date.value, props.modelValue)) {
      return
    }

    const dateToEmit = isInvalidDateObject(date.value) ? null : date.value
    emit('update:modelValue', dateToEmit)
  }
</script>

<style lang="scss" scoped>
  .BirthDatePickerComponent {
    .inputs {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: $gap-xl;

      :deep(.v-field) {
        background-color: $color-grey-200;
        border-radius: $b-radius-0;
        box-shadow: $box-shadow-0;
        transition: all 0.2s ease-in-out;

        .v-field__input {
          font-family: $font-family-primary;
          color: $color-neutral-dark;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }

        /*       &:focus-within {
        box-shadow: $box-shadow-focus;
      } */
      }

      :deep(.v-field--error) {
        background-color: $color-error-200;

        .v-field__input {
          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }
      }
    }

    .full-date {
      // display: none;
      visibility: hidden;
      height: 0;
    }

    label {
      visibility: hidden;
      display: flex;
      max-width: 0;
      max-height: 0;
    }
  }
</style>
